import React, { useEffect, useState } from "react";

/** Matches `entry.js` on CDN: external React UMD vs self-contained React 19+ build. */
type WindowSdkUrl = {
  header?: string;
  footer?: string;
  headerBundled?: string;
  footerBundled?: string;
};

/**
 * CDN `entry.js` ships two UMD variants per part:
 * - external (`sdkUrl.header` / `footer`) — expects `window.React` (exchange / spot, React 18)
 * - bundled (`sdkUrl.headerBundled` / `footerBundled`) — React inlined (blofin-home, React 19+)
 *
 * External UMD embeds React 18 jsx-runtime and breaks on React 19 hosts (`window['blofin-nav-footer'] === {}`).
 * Detect the host app's React major version (`React.version` from the module graph — not
 * `window.React`, which blofin-home does not set) so `resolveNavSdkScripts` picks bundled vs external.
 */
function shouldUseBundledNavUmd(): boolean {
  const major = Number.parseInt(React.version.split(".")[0] ?? "0", 10);
  return Number.isFinite(major) && major >= 19;
}

function resolveNavSdkScripts(raw: WindowSdkUrl | undefined): {
  header: string;
  footer: string;
} {
  if (!raw) {
    return { header: "", footer: "" };
  }
  const useBundled = shouldUseBundledNavUmd();
  const pick = (external?: string, bundled?: string) =>
    useBundled ? (bundled ?? external ?? "") : (external ?? bundled ?? "");

  return {
    header: pick(raw.header, raw.headerBundled),
    footer: pick(raw.footer, raw.footerBundled)
  };
}

const useNav = () => {
  const [sdkUrl, setSdkUrl] = useState({
    header: "",
    footer: ""
  });

  const loadScript = (url: string, init: () => void) => {
    return new Promise<void>((resolve, reject) => {
      if (!url) {
        resolve();
        return;
      }

      const existingEl = Array.from(document.scripts).find(
        (s) => s.src === url || s.getAttribute("src") === url
      );

      if (existingEl) {
        if (existingEl.dataset.loaded === "1") {
          init();
          resolve();
          return;
        }
        const onLoad = () => {
          init();
          existingEl.removeEventListener("load", onLoad);
          resolve();
        };
        existingEl.addEventListener("load", onLoad);
        existingEl.addEventListener(
          "error",
          () => {
            reject(new Error(`Failed to load script: ${url}`));
          },
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = url;
      script.async = true;

      script.onload = () => {
        script.dataset.loaded = "1";
        init();
        resolve();
      };

      script.onerror = (error) => {
        reject(new Error(`Failed to load script: ${url} ${error}`));
      };

      document.body.appendChild(script);
    });
  };

  const initHeader = async (init: () => void) => {
    if (sdkUrl.header) {
      try {
        await loadScript(sdkUrl.header, init);
      } catch (error) {
        console.error("Failed to initialize header:", error);
      }
    }
  };

  const initFooter = async (init: () => void) => {
    if (sdkUrl.footer) {
      try {
        await loadScript(sdkUrl.footer, init);
      } catch (error) {
        console.error("Failed to initialize footer:", error);
      }
    }
  };

  useEffect(() => {
    if (window.sdkUrl) {
      setSdkUrl(resolveNavSdkScripts(window.sdkUrl as WindowSdkUrl));
    }
  }, []);

  return {
    sdkUrl,
    initHeader,
    initFooter
  };
};

export default useNav;
